import React, { useState, useEffect } from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { Card, InputGroup } from 'react-bootstrap';
import CardContent from '@mui/material/CardContent';

import Upload from '../../../../components/Input/Upload';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';
import AlertService from '../../../../components/SweetAlert';
import LayoutContext from '../../../../store/Layout/contexts';
import { newFetch, GET, POST } from '../../../../helpers/fetch';
import { getSelectedSiteIds } from '../../../../helpers/general';
import AppContext from '../../../../store/App/contexts';

const AddLeave = ({ history }) => {
  const accessType = localStorage.getItem('type');
  const {
    fetchClientsOptions,
    client_options,
    selectedClientID,
    fetchSiteOptions,
    selectedSiteID,
  } = React.useContext(AppContext);
  const clientID = parseInt(selectedClientID, 10);
  const siteIDs = getSelectedSiteIds(clientID, selectedSiteID);
  const siteIDsKey = siteIDs.join(',');

  const [remarks, setRemarks] = useState('');
  const [dayType, setDayType] = useState(0);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [files, setFiles] = useState([]);
  const [leaveDropdown, setLeaveDropdown] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showDayType, setShowDayType] = useState(false);
  const [leaveFrom, setLeaveFrom] = useState('');
  const [leaveTo, setLeaveTo] = useState('');
  const [selectedAssociate, setSelectedAssociate] = useState('');
  const [associates, setAssociates] = useState([]);
  const [selectedClient, setSelectedClient] = useState([]);

  useEffect(() => {
    const fetchLeaveDropdown = async () => {
      if (!selectedAssociate) {
        setLeaveDropdown([]);
        return;
      }
      try {
        const apiPath = `/associate-leave-summary/dropdown/${selectedAssociate}`;
        const response = await newFetch(GET, apiPath);
        if (response.success) {
          setLeaveDropdown(response.data);
        } else {
          setLeaveDropdown([]);
          ToastUtil.error('Unable to fetch leave types for this employee');
        }
      } catch (err) {
        console.error('Dropdown fetch error:', err);
      }
    };
    fetchLeaveDropdown();
  }, [selectedAssociate]);

  useEffect(() => {
    fetchAssociates();
  }, [clientID, siteIDsKey]);

  useEffect(() => {
    setSelectedClient(selectedClientID);
    fetchClientDetails(selectedClientID);
  }, [selectedClientID]);

  useEffect(() => {
    fetchClientsOptions(accessType);
  }, []);

  const fetchClientDetails = async (clientID) => {
    try {
      let response = await newFetch(GET, `/client/${clientID}`);
      if (response.success) setSelectedClient(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAssociates = async () => {
    if (!clientID || siteIDs.length === 0) {
      setAssociates([]);
      return;
    }

    try {
      let response = await newFetch(
        GET,
        `/active_associate/active/client/${clientID}?site_ids=${siteIDsKey}`
      );
      if (response.success) setAssociates(response.data);
      else ToastUtil.error(response.message);
    } catch (err) {
      console.error(err);
    }
  };

  const getLeaveSummaryByAssociateId = async () => {
    try {
      const apiPath = `/associate-summary/${selectedAssociate}`;
      const response = await newFetch(GET, apiPath);
      if (response.success !== false) return response;
      ToastUtil.error('Failed to retrieve leave summary.');
      return null;
    } catch (err) {
      ToastUtil.error('Error fetching leave summary.');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAssociate) return ToastUtil.error('Select employee');
    if (!selectedLeaveType) return ToastUtil.error('Select leave type');
    if (!leaveFrom || !leaveTo)
      return ToastUtil.error('Select leave from and to dates');

    const selectedLeave = leaveDropdown.find(
      (item) => item.value === selectedLeaveType
    );
    if (selectedLeave?.allow_document && files.length === 0)
      return ToastUtil.error('Upload the document');

    const summaryResponse = await getLeaveSummaryByAssociateId();
    if (!summaryResponse) return;

    // Access the leave_summary array from the response
    const leaveSummaryList = summaryResponse.leave_summary || [];

    const matchedSummary = leaveSummaryList.find(
      (summary) => summary.leave_type_id === parseInt(selectedLeaveType)
    );

    if (!matchedSummary)
      return ToastUtil.error('No leave summary found for selected type.');

    // ---- Calculate day fraction ----
    let dayFraction = dayType === 0 ? 1 : 0.5;

    // ---- Calculate totalDaysRequested ----
    const startDate = new Date(leaveFrom);
    const endDate = new Date(leaveTo);
    if (startDate > endDate)
      return ToastUtil.error('Leave From date cannot be after Leave To date');

    const daysDiff =
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;
    const totalDaysRequested = daysDiff * dayFraction;

    // ---- Check leave balance ----
    const availableBalance =
      parseFloat(matchedSummary.available) -
      parseFloat(matchedSummary.pending || 0);
    if (availableBalance < totalDaysRequested)
      return ToastUtil.error(
        `Not enough leave balance. Available: ${availableBalance} days, Requested: ${totalDaysRequested} days`
      );

    // ---- Prepare FormData ----
    const formData = new FormData();
    formData.append('associate_id', selectedAssociate);
    formData.append('leave_type_id', parseInt(selectedLeaveType));
    formData.append('start_date', leaveFrom);
    formData.append('end_date', leaveTo);
    formData.append('day_type', dayType);
    formData.append('total_days', totalDaysRequested);
    formData.append('remarks', remarks);
    formData.append('allow_document', selectedLeave?.allow_document);
    formData.append('client_id', clientID);
    formData.append('site_ids', siteIDsKey);

    if (selectedLeave?.allow_document && files[0])
      formData.append('file', files[0]);

    const response = await newFetch(
      POST,
      '/associate-leaves/admin/apply-leave',
      formData,
      'multi'
    );

    if (response.success) {
      ToastUtil.success(response.message || 'Leave submitted successfully!');
      setSelectedLeaveType('');
      setLeaveFrom('');
      setLeaveTo('');
      setDayType(0);
      setFiles([]);
      setRemarks('');
      history.push('/leaves');
    } else {
      ToastUtil.error(response.message || 'Failed to submit leave');
    }
  };

  const handleLeaveTypeChange = (e) => {
    const value = e.target.value;
    setSelectedLeaveType(value);
    const selectedLeave = leaveDropdown.find((item) => item.value === value);
    if (!selectedLeave) {
      setShowFileUpload(false);
      setShowDayType(false);
      return;
    }
    setShowFileUpload(selectedLeave.allow_document === true);
    setShowDayType(selectedLeave.allow_half_day === true);
    setDayType(0);
  };

  const handleDiscard = async () => {
    const isConfirmed = await AlertService.confirm(
      '',
      'Are you sure you want to discard all changes?'
    );
    if (isConfirmed) {
      setSelectedAssociate('');
      setSelectedLeaveType('');
      setLeaveFrom('');
      setLeaveTo('');
      setDayType(0);
      setFiles([]);
      setRemarks('');
    }
  };

  return (
    <LayoutContext.Consumer>
      {({ activeSidebar }) => (
        <div style={{ paddingTop: '3rem' }}>
          <div
            className="d-flex flex-column"
            style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
          >
            <NavLink
              to="/leaves"
              className="text-decoration-none"
              style={{ color: 'inherit' }}
            >
              <div style={{ color: '#A6478E' }}>
                <i className="fas fa-chevron-left pe-2"></i>Back to Leave
              </div>
            </NavLink>
            <div className="pt-4">
              <h5>Apply for Leave</h5>
            </div>
          </div>

          {selectedClient?.enable_leave_credits ? (
            <form className="form" onSubmit={handleSubmit}>
              <div
                style={{
                  marginTop: '1rem',
                  paddingLeft: '3rem',
                  paddingRight: '3rem',
                  paddingBottom: '6rem',
                }}
              >
                <Card>
                  <CardContent>
                    <div className="edit-leave-wrapper">
                      {/* Client */}
                      <div className="mb-4">
                        <strong>Client</strong>
                        <p
                          style={{
                            fontSize: 20,
                            color: '#595b5f',
                            fontWeight: 500,
                            paddingLeft: 8,
                          }}
                        >
                          {client_options &&
                            selectedClientID &&
                            client_options.find(
                              (clnt) => clnt.id === parseInt(selectedClientID)
                            )?.name}
                        </p>
                      </div>

                      <div className="row g-3">
                        {/* Associate */}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Employee Name*
                          </label>
                          <select
                            name="selectedAssociate"
                            className="form-select"
                            value={selectedAssociate}
                            onChange={(e) =>
                              setSelectedAssociate(e.target.value)
                            }
                          >
                            <option value="">Select Employee</option>
                            {associates.map((assoc, index) => (
                              <option
                                key={`${assoc.id}_${index}`}
                                value={assoc.id}
                              >
                                {assoc.first_name + ' ' + assoc.last_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Leave Type */}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Type of Leave*
                          </label>
                          <select
                            value={selectedLeaveType}
                            onChange={handleLeaveTypeChange}
                            className="form-select"
                          >
                            <option value="">Select leave type</option>
                            {leaveDropdown.map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Day Type */}
                        {showDayType && (
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              Day Type
                            </label>
                            <select
                              name="dayType"
                              value={dayType}
                              className="form-select"
                              onChange={(e) =>
                                setDayType(parseFloat(e.target.value))
                              }
                            >
                              <option value="0">Full Day</option>
                              <option value="1">Half Day (AM)</option>
                              <option value="2">Half Day (PM)</option>
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Leave From & Leave To Row */}
                      <div className="row g-3 mt-3">
                        {/* Leave From */}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Leave From*
                          </label>
                          <InputGroup>
                            <Flatpickr
                              className="form-control"
                              placeholder="dd/mm/yyyy"
                              value={leaveFrom}
                              onChange={(date) => setLeaveFrom(date[0])}
                              options={{ dateFormat: 'd/m/Y' }}
                            />
                          </InputGroup>
                        </div>

                        {/* Leave To */}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Leave To*
                          </label>
                          <InputGroup>
                            <Flatpickr
                              className="form-control"
                              placeholder="dd/mm/yyyy"
                              value={leaveTo}
                              onChange={(date) => setLeaveTo(date[0])}
                              options={{
                                dateFormat: 'd/m/Y',
                                minDate: leaveFrom || undefined,
                              }}
                            />
                          </InputGroup>
                        </div>
                      </div>

                      {/* Upload Files */}
                      {showFileUpload && (
                        <div className="row g-3 mt-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              Upload Files*
                            </label>
                            <Upload
                              files={files}
                              setFiles={setFiles}
                              accept="image/jpg, image/jpeg, image/png"
                              variant="button"
                            />
                          </div>
                        </div>
                      )}

                      {/* Remarks */}
                      <div className="row g-3 mt-3">
                        <div className="col-12">
                          <label className="form-label fw-bold">Remarks</label>
                          <textarea
                            className="form-control"
                            rows="5"
                            style={{ resize: 'none' }}
                            placeholder="Remarks"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Footer */}
              <div
                style={{
                  position: 'fixed',
                  bottom: 0,
                  width: activeSidebar ? 'calc(100vw - 310px)' : '100vw',
                  height: '4.5rem',
                  paddingRight: '3rem',
                }}
                className="border-top bg-white d-flex align-items-center justify-content-end"
              >
                <button
                  type="button"
                  className="btn btn-outline-brand"
                  style={{ width: '12rem' }}
                  onClick={handleDiscard}
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="btn btn-brand ms-3"
                  style={{ width: '12rem' }}
                >
                  Apply Leave
                </button>
              </div>
            </form>
          ) : (
            <div
              className="text-muted pt-4"
              style={{
                marginTop: '1rem',
                paddingLeft: '3rem',
                paddingRight: '3rem',
                paddingBottom: '6rem',
              }}
            >
              The client does not have access to this module.
            </div>
          )}
        </div>
      )}
    </LayoutContext.Consumer>
  );
};

export default withRouter(AddLeave);
